import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { motion, AnimatePresence } from 'framer-motion'
import { FaBriefcase, FaCheckCircle, FaTimesCircle, FaMoneyBillWave } from 'react-icons/fa'
import OpportunityCard from './OpportunityCard'
import { designTokens } from '@/styles/designTokens'

const PanelContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  padding: 0;
  margin: 0;
`

const PanelHeader = styled.div`
  padding: 10px 15px 10px 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
  margin: 0;
  
  h3 {
    color: #00d4ff;
    margin-bottom: 8px;
    font-size: 20px;
    font-weight: 700;
    text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
  }
`

const StatusLegend = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;
  padding: 15px;
  background: rgba(43, 43, 43, 0.3);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 100%;
  font-size: 18px;
  font-weight: 500;
  color: ${props => {
    switch (props.$status) {
      case 'pending': return designTokens.colors.status.warning
      case 'accepted': return designTokens.colors.status.info
      case 'billed': return designTokens.colors.status.success
      default: return designTokens.colors.status.neutral
    }
  }};
`

// Removido o margin-bottom e centralização vertical para ocupar todo o espaço disponível
const CardDisplayArea = styled.div`
  flex: 1 1 0;
  display: flex;
  align-items: flex-start;
  justify-content: stretch;
  min-height: 0;
  position: relative;
  padding: 0;
  margin: 0;
  padding-bottom: 10px;
`

// Cards agora ocupam todo o espaço vertical disponível
const AnimatedCard = styled(motion.div)`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 8px;
  padding: 0;
  min-height: 0;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100px;
  color: #888;
  font-size: 14px;
  
  svg {
    font-size: 48px;
    margin-bottom: 15px;
    opacity: 0.5;
  }
`

const ScrollIndicator = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  align-items: center;
  padding: 8px;
  background: rgba(43, 43, 43, 0.3);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
`

const IndicatorDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.$active ? '#00d4ff' : 'rgba(255, 255, 255, 0.3)'};
  transition: all 0.3s ease;
`

const OpportunitiesPanel = ({ opportunities = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0)

  // Log para debug - verificar quantas oportunidades estão chegando
  console.log('🔍 OpportunitiesPanel recebeu:', opportunities.length, 'oportunidades')
  console.log('📋 Lista completa de oportunidades:', opportunities.map(o => ({
    id: o.id,
    client: o.client,
    empresa_cliente: o.empresa_cliente,
    exibido: o.exibido,
    status: o.status
  })))

  // Ordenar oportunidades por data (mais recente primeiro)
  const sortedOpportunities = useMemo(() => {
    console.log('Oportunidades recebidas para ordenação:', opportunities.map(o => ({
      id: o.id,
      data: o.data,
      data_iso: o.data_iso,
      created_at: o.created_at,
      updated_at: o.updated_at,
      titulo: o.titulo || o.title || o.client
    })))
    
    const sorted = [...opportunities].sort((a, b) => {
      // Priorizar data_iso (formato ISO) se disponível, senão usar created_at
      let dateFieldA = a.data_iso || a.created_at || a.updated_at
      let dateFieldB = b.data_iso || b.created_at || b.updated_at
      
      // Se ainda não temos data_iso e temos o campo 'data' em formato brasileiro, tentar converter
      if (!dateFieldA && a.data) {
        try {
          // Formato esperado: 25/07/25 16:49:00
          const parts = a.data.match(/(\d{2})\/(\d{2})\/(\d{2}) (\d{2}):(\d{2}):(\d{2})/)
          if (parts) {
            // Assumir que anos 00-30 são 2000-2030, 31-99 são 1931-1999
            const year = parseInt(parts[3]) <= 30 ? 2000 + parseInt(parts[3]) : 1900 + parseInt(parts[3])
            dateFieldA = `${year}-${parts[2]}-${parts[1]}T${parts[4]}:${parts[5]}:${parts[6]}`
          }
        } catch (e) {
          console.warn('Erro ao converter data brasileira:', a.data, e)
        }
      }
      
      if (!dateFieldB && b.data) {
        try {
          // Formato esperado: 25/07/25 16:49:00
          const parts = b.data.match(/(\d{2})\/(\d{2})\/(\d{2}) (\d{2}):(\d{2}):(\d{2})/)
          if (parts) {
            // Assumir que anos 00-30 são 2000-2030, 31-99 são 1931-1999
            const year = parseInt(parts[3]) <= 30 ? 2000 + parseInt(parts[3]) : 1900 + parseInt(parts[3])
            dateFieldB = `${year}-${parts[2]}-${parts[1]}T${parts[4]}:${parts[5]}:${parts[6]}`
          }
        } catch (e) {
          console.warn('Erro ao converter data brasileira:', b.data, e)
        }
      }
      
      // Verificar se as datas existem e são válidas
      const dateA = dateFieldA ? new Date(dateFieldA) : new Date(0)
      const dateB = dateFieldB ? new Date(dateFieldB) : new Date(0)
      
      console.log(`Comparando: A(${a.data} -> ${dateFieldA}) = ${dateA.toISOString()} vs B(${b.data} -> ${dateFieldB}) = ${dateB.toISOString()}`)
      
      // Se as datas são inválidas, usar o ID como fallback
      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
        console.log('Usando ID como fallback')
        return (b.id || 0) - (a.id || 0)
      }
      
      // Ordenar por data: mais nova primeiro (dateB - dateA)
      const comparison = dateB.getTime() - dateA.getTime()
      console.log(`Resultado da comparação: ${comparison} (${comparison > 0 ? 'B mais nova' : 'A mais nova'})`)
      return comparison
    })
    
    console.log('Resultado final da ordenação (mais nova -> mais velha):', 
      sorted.map(o => ({ 
        id: o.id, 
        data_original: o.data,
        data_iso: o.data_iso,
        titulo: o.titulo || o.title || o.client 
      })))
    return sorted
  }, [opportunities])

  useEffect(() => {
    if (!sortedOpportunities.length) return
    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const nextIndex = prev + 5
        return nextIndex >= sortedOpportunities.length ? 0 : nextIndex
      })
    }, 32000)
    return () => clearInterval(interval)
  }, [sortedOpportunities.length])

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <FaBriefcase />
      case 'accepted': return <FaCheckCircle /> 
      case 'billed': return <FaMoneyBillWave />
      default: return <FaTimesCircle />
    }
  }

  return (
    <PanelContainer>
      <CardDisplayArea>
        <AnimatePresence mode="wait">
          {sortedOpportunities.length > 0 ? (
            <AnimatedCard
              key={currentIndex}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.6 }}
            >
              {sortedOpportunities
                .slice(currentIndex, currentIndex + 5)
                .map((opportunity, index) => (
                  <OpportunityCard 
                    key={opportunity.id} 
                    opportunity={opportunity} 
                  />
                ))}
            </AnimatedCard>
          ) : (
            <EmptyState>
              <div style={{ alignItems: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column' }}>
                <FaBriefcase />
                <p>Nenhuma oportunidade disponível</p>
              </div>
            </EmptyState>
          )}
        </AnimatePresence>
      </CardDisplayArea>
      
      {sortedOpportunities.length > 5 && (
        <ScrollIndicator>
          {Array.from({ length: Math.ceil(sortedOpportunities.length / 5) }, (_, i) => (
            <IndicatorDot 
              key={i} 
              $active={Math.floor(currentIndex / 5) === i} 
            />
          ))}
        </ScrollIndicator>
      )}

      <StatusLegend>
      <LegendItem $status="accepted">
          {getStatusIcon('pending')}
          Pedido
        </LegendItem>
        <LegendItem $status="pending">
          {getStatusIcon('accepted')}
          Aceita
        </LegendItem>
        <LegendItem $status="billed">
          {getStatusIcon('billed')}
          Faturada
        </LegendItem>
      </StatusLegend>
    </PanelContainer>
  )
}

export default OpportunitiesPanel 